import React, {useRef, useImperativeHandle, forwardRef} from 'react';
import {
  FlatList,
  Image,
  ImageBackground,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import {PapersForPhaseTestingResItem} from '../../../../API/mylearn';
import {createStyles} from '../../../../utils/createStyles';
import {useAppSelector} from '../../../../store/hooks';
import {selectGrades, selectPaperTypes} from '../../../../store/xueke';
import {provinceList} from './province';

interface TestListProps {
  testList: PapersForPhaseTestingResItem[];
  loadMore: () => void;
  submitPaper: (item: PapersForPhaseTestingResItem) => void;
  footer?: () => JSX.Element;
}

const TestList = forwardRef(
  ({testList, loadMore, submitPaper, footer}: TestListProps, ref) => {
    const listRef = useRef(null);
    const grades = useAppSelector(selectGrades);
    const paperType = useAppSelector(selectPaperTypes);

    useImperativeHandle(ref, () => ({
      scrollToTop: () => {
        // @ts-ignore
        listRef.current?.scrollToOffset({offset: 0, animated: true});
      },
    }));

    return (
      <FlatList
        ref={listRef}
        data={testList}
        keyExtractor={(item, index) => item.id + '-' + index}
        onEndReached={loadMore}
        ListEmptyComponent={() => {
          return (
            <View style={[styles.TestListItemEmpty]}>
              <Image
                style={styles.TestListItemEmptyImage}
                source={{
                  uri: 'https://media.oss.leziedu.com/webapp/classroom/mylearn/empty.png',
                }}
              />
              <Text style={styles.TestListItemEmptyText}>暂无</Text>
            </View>
          );
        }}
        ListFooterComponent={footer}
        onEndReachedThreshold={0.1}
        renderItem={({item}) => {
          function convertToJSX(str: string) {
            const regex = /<em>(.*?)<\/em>/g;
            let match;
            const elements = [];
            let lastIndex = 0;

            while ((match = regex.exec(str)) !== null) {
              if (match.index !== lastIndex) {
                elements.push(str.substring(lastIndex, match.index));
              }

              elements.push(
                <Text key={match.index} style={{color: 'red'}}>
                  {match[1]}
                </Text>,
              );
              lastIndex = regex.lastIndex;
            }

            if (lastIndex !== str.length) {
              elements.push(str.substring(lastIndex));
            }

            return elements;
          }
          return (
            <View style={styles.TestListItem} key={item.id}>
              <ImageBackground
                style={styles.TestListItemLogo}
                source={{
                  uri: 'https://media.oss.leziedu.com/webapp/classroom/mylearn/bk/mylearn_beikao_testlogo.png',
                }}>
                <Text style={styles.TestListItemLogoText}>{'试卷'}</Text>
              </ImageBackground>
              <View style={styles.TestListItemInfo}>
                <Text
                  numberOfLines={1}
                  ellipsizeMode="tail"
                  style={styles.TestListItemInfoText}>
                  {convertToJSX(item.title)}
                </Text>
                <View style={styles.TestListItemInfoDesc}>
                  <Text
                    style={[
                      styles.TestListItemInfoDescText,
                      {backgroundColor: '#FFE9E8'},
                    ]}>
                    {paperType.find(i => i.id === item.type_id)?.name || '试卷'}
                  </Text>
                  <Text
                    style={[
                      styles.TestListItemInfoDescText,
                      {backgroundColor: '#FFF4D5'},
                    ]}>
                    {grades.find(i => i.id === item.grade_id)?.name || '年级'}
                  </Text>
                  <Text
                    style={[
                      styles.TestListItemInfoDescText,
                      {backgroundColor: '#FEFFDB'},
                    ]}>
                    {item.year}
                  </Text>
                  {item.area_ids && (
                    <Text
                      style={[
                        styles.TestListItemInfoDescText,
                        {backgroundColor: '#ECFFDB'},
                      ]}>
                      {provinceList.find(i => {
                        let area_id = '000000';
                        if (+JSON.parse(item.area_ids)[0] === 1) {
                          area_id = '1';
                        } else {
                          area_id =
                            JSON.parse(item.area_ids)[0].substring(0, 2) +
                            '0000';
                        }
                        if (i.id + '' === area_id) {
                          return i;
                        }
                      })?.name || '全国'}
                    </Text>
                  )}
                  {item.org_name && (
                    <Text
                      numberOfLines={1}
                      ellipsizeMode="tail"
                      style={[
                        styles.TestListItemInfoDescText,
                        styles.TestListItemInfoDescTextMax,
                        {backgroundColor: '#E3FFF5'},
                      ]}>
                      {item.org_name}
                    </Text>
                  )}
                  <Text
                    style={[
                      styles.TestListItemInfoDescText,
                      {backgroundColor: '#F2F0FF'},
                    ]}>
                    题量{item.question_count}
                  </Text>
                </View>
              </View>
              <TouchableOpacity
                onPress={() => {
                  submitPaper(item);
                }}>
                <Image
                  style={styles.TestListItemDownload}
                  source={{
                    uri: 'https://media.oss.leziedu.com/webapp/classroom/mylearn/bk/mylearn_beikao_testdownload.png',
                  }}
                />
              </TouchableOpacity>
            </View>
          );
        }}
      />
    );
  },
);

const styles = createStyles({
  TestListItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#fff',
    paddingHorizontal: '1.25rem',
    height: '7.7rem',
    borderRadius: '0.75rem',
    marginBottom: '1.1rem',
  },
  TestListItemEmpty: {
    // flex: 1,
    marginTop: 100,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  TestListItemEmptyImage: {
    width: '19.05rem',
    height: '15rem',
  },
  TestListItemEmptyText: {
    fontSize: '1.4rem',
    color: '#666666',
    marginLeft: '2.85rem',
  },
  TestListItemLogo: {
    width: '4.8rem',
    height: '5rem',
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap',
    paddingLeft: '1.75rem',
    paddingRight: '0.8rem',
  },
  TestListItemLogoText: {
    fontSize: '0.8rem',
    color: '#fff',
  },
  TestListItemDownload: {
    width: '4.5rem',
    height: '4.5rem',
    marginRight: '0.5rem',
  },
  TestListItemInfo: {
    flex: 1,
    marginLeft: '1.85rem',
  },
  TestListItemInfoText: {
    width: '94%',
    fontSize: '1.4rem',
    color: '#3D3D3D',
  },
  TestListItemInfoDesc: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: '0.9rem',
  },
  TestListItemInfoDescText: {
    fontSize: '1rem',
    color: '#777777',
    minWidth: '5.2rem',
    paddingHorizontal: '1rem',
    height: '1.9rem',
    textAlign: 'center',
    lineHeight: '1.9rem',
    marginRight: '0.5rem',
    borderRadius: '0.35rem',
  },
  TestListItemInfoDescTextMax: {
    maxWidth: '15rem',
  },
});

export default TestList;
